import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonEditCard } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { Card } from "@shared/models/card-stack"
import Patch from "@shared/models/patch-updater"
import { assign, get, map } from "lodash-es"
import type { Narrative } from "../create-narrative"
import defaultLessonActivity from "./default-lesson-activity"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"

interface Payload {
  cardStackId: string
  positionDiffs: Array<string>
  newCards: Array<string>
  sectionBeingMoved: Card
}

export const LESSON_CARD_MOVE_WITHIN_LESSON = {
  name: "LESSON_CARD_MOVE_WITHIN_LESSON",

  patches(payload: Payload) {
    return map(payload.positionDiffs, (tuple) => {
      return Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
        id: tuple[0],
      }).set("embedded", "attributes.position", tuple[1]).patch
    })
  },

  undoPatches(payload: Payload) {
    return map(payload.positionDiffs, (tuple) => {
      return Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
        id: tuple[0],
      }).set("embedded", "attributes.position", tuple[2]).patch
    })
  },

  narrative(payload: Payload, findFn: FindFn, userId): Narrative {
    let models = defaultLessonModels(payload, findFn, userId)
    let cardTitle = get(payload, "sectionBeingMoved.attributes.title") || "Untitled"
    let context = defaultLessonContext(payload, models)
    let newContext = assign({}, context, {
      moveType: "withinLesson",
      cardTitle: cardTitle,
    })
    let newEvent: LessonEditCard = {
      ...createBlankEvent(),
      root_id: models.planbook?.id ?? null,
      root_type: "planbook",
      date_plan_scheduled_for: models.dateStringForCardStack,
      primary_object_id: payload.cardStackId,
      primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
      primary_verb: "edit",
      primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook?.id),
      primary_object_type: "lesson",
      primary_object_specifier: "cards",

      adverbial_verb: "dragging_dropping",
      adverbial_object_type: "card",
      adverbial_object_specifier: "within_lesson",
      adverbial_object_id: payload.sectionBeingMoved.id,
      adverbial_possessive: null,
      adverbial_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
    }

    return {
      title: "Moved a Lesson Card",
      titleForAnalytics: "Lesson Card - Move",
      description: `Moved "${cardTitle}" card`,
      context: newContext,
      activity: defaultLessonActivity(payload, models, "moved"),
      newAnalyticsEvents: [newEvent],
    }
  },
}

export default LESSON_CARD_MOVE_WITHIN_LESSON
